import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChartContainer } from "@/components/ui/chart";
import { useToast } from "@/hooks/use-toast";
import { DollarSign, Users, Search, Shield, RefreshCw, Star, CalendarPlus, TrendingUp, BarChart3, AlertTriangle, Crown, ShieldCheck, UserPlus, ExternalLink } from "lucide-react";
import { FounderBugReportsPanel } from "@/components/founder/FounderBugReportsPanel";
import { BroadcastsPanel } from "@/components/founder/BroadcastsPanel";
import { FeatureFlagsPanel } from "@/components/founder/FeatureFlagsPanel";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ComposedChart, Legend, Tooltip, PieChart, Pie, Cell } from "recharts";

interface UserRow {
  user_id: string;
  email: string | null;
  full_name: string | null;
  business_id: string | null;
  business_name: string | null;
  subscription_status: string | null;
  subscription_valid_until: string | null;
  subscription_plan: string | null;
  is_founding_member: boolean;
  locked_monthly_rate: number | null;
  is_vip: boolean;
  product_count: number;
}

type PlanLabel = "Free" | "Pro Solo" | "Pro Growth" | "Grace" | "VIP";

interface BusinessSummary {
  user_id: string;
  business_id: string;
  business_name: string | null;
  email: string | null;
  full_name: string | null;
  subscription_status: string | null;
  subscription_valid_until: string | null;
  subscription_plan: string | null;
  is_founding_member: boolean;
  locked_monthly_rate: number | null;
  is_vip: boolean;
  product_count: number;
  planLabel: PlanLabel;
  expiryDays: number | null;
}

const PRO_SOLO_MRR = 900;
const PRO_GROWTH_MRR = 1800;
const FOUNDING_TOTAL = 50;

function isActivePaidRow(row: Pick<UserRow, "is_vip" | "subscription_status" | "subscription_plan" | "subscription_valid_until">, now = new Date()): boolean {
  if (row.is_vip) return true;
  const status = (row.subscription_status ?? "").toLowerCase();
  const plan = (row.subscription_plan ?? "").toLowerCase();
  const validUntil = row.subscription_valid_until ? new Date(row.subscription_valid_until) : null;
  const valid =
    !validUntil ||
    Number.isNaN(validUntil.getTime()) ||
    now.getTime() <= validUntil.getTime() + 24 * 60 * 60 * 1000;
  return valid && (status === "active" || status === "pro" || status === "grace") && (plan === "pro_solo" || plan === "pro_growth");
}

function getPlanPriority(row: UserRow): number {
  if (row.is_vip) return 5;
  const status = (row.subscription_status ?? "").toLowerCase();
  const plan = (row.subscription_plan ?? "").toLowerCase();
  if (status === "grace" && (plan === "pro_solo" || plan === "pro_growth")) return 4;
  if (status === "active" || status === "pro") {
    if (plan === "pro_growth") return 3;
    if (plan === "pro_solo") return 2;
  }
  return 1;
}

function getPlanLabel(row: UserRow): PlanLabel {
  if (row.is_vip) return "VIP";
  if (!isActivePaidRow(row)) return "Free";
  const s = (row.subscription_status ?? "").toLowerCase();
  if (s === "grace") return "Grace";
  const plan = (row.subscription_plan ?? "").toLowerCase();
  if (plan === "pro_growth") return "Pro Growth";
  if (plan === "pro_solo") return "Pro Solo";
  return "Free";
}

function daysUntilExpiry(validUntil: string | null): number | null {
  if (!validUntil) return null;
  const end = new Date(validUntil);
  const now = new Date();
  return Math.ceil((end.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
}

/** Compute MRR from list: one row per business, use locked_monthly_rate or default by plan */
function buildBusinessSummaries(rows: UserRow[]): BusinessSummary[] {
  const byBusiness = new Map<string, UserRow>();
  rows.forEach((row) => {
    if (!row.business_id) return;
    const existing = byBusiness.get(row.business_id);
    if (!existing || getPlanPriority(row) > getPlanPriority(existing)) {
      byBusiness.set(row.business_id, row);
      return;
    }
    if (existing) {
      byBusiness.set(row.business_id, {
        ...existing,
        product_count: Math.max(existing.product_count, row.product_count),
        business_name: existing.business_name ?? row.business_name,
        email: existing.email ?? row.email,
        full_name: existing.full_name ?? row.full_name,
      });
    }
  });
  return Array.from(byBusiness.values()).map((row) => ({
    ...row,
    business_id: row.business_id!,
    planLabel: getPlanLabel(row),
    expiryDays: daysUntilExpiry(row.subscription_valid_until),
  }));
}

function computeMrrFromBusinesses(rows: BusinessSummary[]): number {
  return rows.reduce((total, row) => {
    if (row.planLabel === "Free") return total;
    if (row.locked_monthly_rate != null) return total + row.locked_monthly_rate;
    if (row.planLabel === "Pro Growth" || row.planLabel === "VIP") return total + PRO_GROWTH_MRR;
    return total + PRO_SOLO_MRR;
  }, 0);
}

interface FounderDashboardPageProps {
  section?: string;
}

function getSectionFromPath(pathname: string): string | undefined {
  if (pathname === "/admin" || pathname === "/admin/") return undefined;
  const m = pathname.match(/^\/admin\/([^/]+)/);
  return m ? m[1] : undefined;
}

export default function FounderDashboardPage({ section: sectionProp }: FounderDashboardPageProps) {
  const { pathname } = useLocation();
  const section = sectionProp ?? getSectionFromPath(pathname);
  const { toast } = useToast();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [revenueByMonth, setRevenueByMonth] = useState<{ month: string; total_k: number; payment_count: number }[]>([]);
  const [funnelByMonth, setFunnelByMonth] = useState<{ month: string; signups: number; upgrades: number }[]>([]);
  const [funnel, setFunnel] = useState<{ signups: number; paid: number; pct: number } | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const businesses = useMemo(() => buildBusinessSummaries(users), [users]);
  const mrr = useMemo(() => computeMrrFromBusinesses(businesses), [businesses]);
  const arr = mrr * 12;
  const paidBusinesses = useMemo(() => businesses.filter((row) => row.planLabel !== "Free"), [businesses]);
  const proCount = paidBusinesses.length;
  const proGrowthCount = useMemo(() => businesses.filter((row) => row.planLabel === "Pro Growth" || row.planLabel === "VIP").length, [businesses]);
  const freeBusinessCount = useMemo(() => businesses.filter((row) => row.planLabel === "Free").length, [businesses]);
  const graceCount = useMemo(() => businesses.filter((row) => row.planLabel === "Grace").length, [businesses]);
  const vipCount = useMemo(() => businesses.filter((row) => row.is_vip).length, [businesses]);
  const foundingCount = useMemo(() => businesses.filter((row) => row.is_founding_member).length, [businesses]);
  const foundingSpotsRemaining = Math.max(0, FOUNDING_TOTAL - foundingCount);
  const renewalsDueSoon = useMemo(
    () => businesses.filter((row) => row.planLabel !== "Free" && row.expiryDays !== null && row.expiryDays >= 0 && row.expiryDays <= 7).sort((a, b) => (a.expiryDays ?? 999) - (b.expiryDays ?? 999)),
    [businesses]
  );
  const churnRiskCount = useMemo(
    () => businesses.filter((row) => row.planLabel === "Grace" || (row.planLabel !== "Free" && row.expiryDays !== null && row.expiryDays <= 7)).length,
    [businesses]
  );
  const arpu = proCount > 0 ? mrr / proCount : 0;
  const latestRevenue = revenueByMonth[0]?.total_k ?? 0;
  const previousRevenue = revenueByMonth[1]?.total_k ?? 0;
  const revenueGrowthPct = previousRevenue > 0 ? ((latestRevenue - previousRevenue) / previousRevenue) * 100 : latestRevenue > 0 ? 100 : 0;
  const filtered = useMemo(
    () =>
      businesses.filter(
        (u) =>
          !search ||
          (u.email?.toLowerCase().includes(search.toLowerCase()) ||
            u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
            u.business_name?.toLowerCase().includes(search.toLowerCase()))
      ),
    [businesses, search]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: listData } = await supabase.rpc("get_founder_user_list");
      const rows: UserRow[] = (listData ?? []).map((r: Record<string, unknown>) => ({
        user_id: r.user_id as string,
        email: (r.email as string) ?? null,
        full_name: (r.full_name as string) ?? null,
        business_id: (r.business_id as string) ?? null,
        business_name: (r.business_name as string) ?? null,
        subscription_status: (r.subscription_status as string) ?? null,
        subscription_valid_until: (r.subscription_valid_until as string) ?? null,
        subscription_plan: (r.subscription_plan as string) ?? null,
        is_founding_member: Boolean(r.is_founding_member),
        locked_monthly_rate: r.locked_monthly_rate != null ? Number(r.locked_monthly_rate) : null,
        is_vip: Boolean(r.is_vip),
        product_count: Number(r.product_count ?? 0),
      }));
      setUsers(rows);

      const { count: signupCount } = await supabase.from("conversion_events").select("id", { count: "exact", head: true }).eq("event_type", "signup_completed");
      const { count: paidCount } = await supabase.from("conversion_events").select("id", { count: "exact", head: true }).eq("event_type", "upgrade_completed");
      const signups = signupCount ?? 0;
      const paid = paidCount ?? 0;
      setFunnel({ signups, paid, pct: signups > 0 ? Math.round((paid / signups) * 100) : 0 });

      const { data: revData } = await supabase.rpc("get_founder_revenue_by_month");
      setRevenueByMonth((revData ?? []).map((r: { month: string; total_k: number; payment_count: number }) => ({
        month: r.month,
        total_k: Number(r.total_k ?? 0),
        payment_count: Number(r.payment_count ?? 0),
      })));

      const { data: funnelData } = await supabase.rpc("get_founder_funnel_by_month");
      setFunnelByMonth((funnelData ?? []).map((r: { month: string; signups: number; upgrades: number }) => ({
        month: r.month,
        signups: Number(r.signups ?? 0),
        upgrades: Number(r.upgrades ?? 0),
      })));
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const handleSetVip = useCallback(async (row: UserRow, isVip: boolean) => {
    const note = window.prompt("Note (optional):");
    if (note === null) return;
    setActingId(row.user_id);
    try {
      await supabase.rpc("founder_set_vip", { p_user_id: row.user_id, p_is_vip: isVip, p_note: note || null });
      toast({ title: "Updated", description: isVip ? "VIP enabled" : "VIP removed" });
      await fetchData();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setActingId(null);
    }
  }, [toast, fetchData]);

  const handleExtendGrace = useCallback(async (row: UserRow) => {
    setActingId(row.user_id);
    try {
      await supabase.rpc("founder_extend_grace", { p_user_id: row.user_id });
      toast({ title: "Extended", description: "Grace +3 days" });
      await fetchData();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setActingId(null);
    }
  }, [toast, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartRevenue = useMemo(() => [...revenueByMonth].reverse(), [revenueByMonth]);
  const chartFunnel = useMemo(() => [...funnelByMonth].reverse(), [funnelByMonth]);

  const STALE_MS = 5 * 60 * 1000;
  const { data: platformHealth } = useQuery({
    queryKey: ["founder-platform-health"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_platform_health");
      return (data ?? [])[0] as { total_businesses: number; active_subscriptions: number; mrr_zmw: number; new_this_month: number; churn_risk: number } | null;
    },
    staleTime: STALE_MS,
  });
  const { data: dailyActive } = useQuery({
    queryKey: ["founder-daily-active"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_daily_active");
      return (data ?? []) as { day_date: string; active_count: number }[];
    },
    staleTime: STALE_MS,
  });
  const { data: recentlyActive } = useQuery({
    queryKey: ["founder-recently-active"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_recently_active");
      return (data ?? []) as { business_id: string; business_name: string | null; plan_label: string; last_activity_at: string; last_activity_type: string }[];
    },
    staleTime: STALE_MS,
  });
  const { data: churnRisk } = useQuery({
    queryKey: ["founder-churn-risk"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_churn_risk");
      return (data ?? []) as { business_id: string; business_name: string | null; plan_label: string; days_inactive: number; last_active_date: string }[];
    },
    staleTime: STALE_MS,
  });
  const { data: newSignups } = useQuery({
    queryKey: ["founder-new-signups"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_new_signups");
      return (data ?? []) as { business_id: string; business_name: string | null; owner_email: string | null; plan_at_signup: string; created_at: string; has_activity: boolean }[];
    },
    staleTime: STALE_MS,
  });
  const { data: systemStatus } = useQuery({
    queryKey: ["founder-system-status"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_system_status");
      return (data ?? [])[0] as { grace_count: number; failed_payments_this_month: number; last_audit_at: string | null; last_audit_action: string | null } | null;
    },
    staleTime: STALE_MS,
  });
  const { data: bugReportsList } = useQuery({
    queryKey: ["founder-bug-reports"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_founder_bug_reports");
      return (data ?? []) as { id: string; business_id: string | null; user_id: string; description: string | null; screenshot_url: string | null; created_at: string }[];
    },
    staleTime: STALE_MS,
    enabled: section === "bug-reports",
  });
  const { data: auditLogRows } = useQuery({
    queryKey: ["founder-audit-log"],
    queryFn: async () => {
      const { data } = await supabase.from("admin_audit").select("id, admin_user_id, action, target_type, target_id, note, created_at").order("created_at", { ascending: false }).limit(100);
      return data ?? [];
    },
    staleTime: STALE_MS,
    enabled: section === "audit-log",
  });

  const todayActiveCount = useMemo(() => {
    if (!dailyActive?.length) return 0;
    const today = new Date().toISOString().slice(0, 10);
    const row = dailyActive.find((r) => r.day_date === today);
    return row?.active_count ?? 0;
  }, [dailyActive]);

  const sectionTitles: Record<string, { title: string; desc: string }> = {
    "businesses": { title: "Business Portfolio", desc: "One row per business, with plan, renewal, product adoption, and founder actions." },
    "revenue": { title: "Revenue", desc: "MRR, ARR, revenue by month, and signup/upgrade funnel." },
    "bug-reports": { title: "Bug Reports", desc: "User-submitted bug reports for review." },
    "broadcasts": { title: "Broadcasts", desc: "Platform-wide announcements and messages." },
    "audit-log": { title: "Audit Log", desc: "Founder and admin action history." },
    "settings": { title: "Platform Settings", desc: "Founder-level platform configuration." },
    "plan-overrides": { title: "Plan Overrides", desc: "Renewal watchlist, VIP, grace extensions, and plan overrides." },
    "feature-flags": { title: "Feature Flags", desc: "Enable or disable platform features." },
  };
  const { title: pageTitle, desc: pageDesc } = section ? (sectionTitles[section] ?? { title: "Founder Dashboard", desc: "SaaS health, revenue quality, renewals at risk, and upgrade signals across your customer base." }) : { title: "Founder Dashboard", desc: "SaaS health, revenue quality, renewals at risk, and upgrade signals across your customer base." };

  const showOverview = !section || section === "overview";
  const showBusinesses = showOverview || section === "businesses";
  const showRevenue = showOverview || section === "revenue";
  const showBugReports = section === "bug-reports";
  const showBroadcasts = section === "broadcasts";
  const showAuditLog = section === "audit-log";
  const showSettings = section === "settings";
  const showPlanOverrides = section === "plan-overrides";
  const showFeatureFlags = section === "feature-flags";

  return (
    <div data-testid="admin-dashboard" className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">{pageTitle}</h1>
          <p className="text-sm text-muted-foreground">{pageDesc}</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {showBugReports && <FounderBugReportsPanel rows={bugReportsList} />}

      {showBroadcasts && <BroadcastsPanel />}

      {/* Audit Log section */}
      {showAuditLog && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Audit Log</CardTitle>
            <CardDescription>Recent founder and admin actions.</CardDescription>
          </CardHeader>
          <CardContent>
            {auditLogRows?.length ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogRows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-sm text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
                        <TableCell>{r.action}</TableCell>
                        <TableCell>{r.target_type} {r.target_id ? `: ${r.target_id}` : ""}</TableCell>
                        <TableCell>{r.note ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : <p className="text-sm text-muted-foreground">No audit entries yet</p>}
          </CardContent>
        </Card>
      )}

      {/* Platform Settings section */}
      {showSettings && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">Platform Settings</CardTitle>
              <CardDescription>Founder-level configuration for the platform.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border p-4 space-y-2">
                <h4 className="font-medium text-sm">Maintenance Mode</h4>
                <p className="text-sm text-muted-foreground">Temporarily disable logins and show a maintenance message. Coming soon.</p>
              </div>
              <div className="rounded-lg border p-4 space-y-2">
                <h4 className="font-medium text-sm">Support & Contact</h4>
                <p className="text-sm text-muted-foreground">Configure support email and contact details shown to users. Coming soon.</p>
              </div>
              <div className="rounded-lg border p-4 space-y-2">
                <h4 className="font-medium text-sm">Currency & Billing</h4>
                <p className="text-sm text-muted-foreground">Default currency, tax settings, and billing configuration. Coming soon.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showFeatureFlags && <FeatureFlagsPanel />}

      {/* SECTION 1 — Platform Health Bar (Overview only) */}
      {showOverview && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Businesses</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{platformHealth?.total_businesses ?? "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{platformHealth?.active_subscriptions ?? "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">MRR (ZMW)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">K{platformHealth?.mrr_zmw != null ? Number(platformHealth.mrr_zmw).toLocaleString() : "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">New This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{platformHealth?.new_this_month ?? "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Churn Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{platformHealth?.churn_risk ?? "—"}</p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* SECTION 3 — User Activity (Overview only) */}
      {showOverview && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Daily Active Businesses</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{todayActiveCount}</p>
            {dailyActive && dailyActive.length > 0 && (
              <div className="mt-2 h-8 flex items-end gap-0.5">
                {dailyActive.slice(-7).map((r) => (
                  <div key={r.day_date} className="flex-1 bg-primary/30 rounded-t min-h-[4px]" style={{ height: `${Math.max(4, (Number(r.active_count) / Math.max(1, Math.max(...dailyActive.map((x) => Number(x.active_count))))) * 32)}px` }} title={`${r.day_date}: ${r.active_count}`} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Recently Active</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[200px] overflow-y-auto">
            {recentlyActive?.length ? recentlyActive.map((r) => (
              <div key={r.business_id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                <div>
                  <p className="font-medium">{r.business_name ?? "—"}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px]">{r.plan_label}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(r.last_activity_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            )) : <p className="text-sm text-muted-foreground">No recent activity</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Churn Risk</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[200px] overflow-y-auto">
            {churnRisk?.length ? churnRisk.map((r) => (
              <div key={r.business_id} className="text-sm border-b pb-2 last:border-0">
                <p className="font-medium">{r.business_name ?? "—"}</p>
                <p className="text-xs text-muted-foreground">{r.plan_label} · {r.days_inactive} days inactive · Last: {r.last_active_date}</p>
              </div>
            )) : <p className="text-sm text-muted-foreground">None</p>}
          </CardContent>
        </Card>
      </div>
      )}

      {showOverview && (
      <Card>
        <CardHeader>
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscription Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {businesses.length > 0 ? (
            <div className="flex items-center gap-6">
              <PieChart width={160} height={160}>
                <Pie data={[
                  { name: "Free", value: freeBusinessCount, color: "hsl(var(--muted-foreground))" },
                  { name: "Pro Solo", value: Math.max(0, proCount - proGrowthCount), color: "hsl(217, 91%, 60%)" },
                  { name: "Pro Growth", value: proGrowthCount, color: "hsl(142, 76%, 36%)" },
                  { name: "Founding", value: foundingCount, color: "hsl(38, 92%, 50%)" },
                ]} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {[0, 1, 2, 3].map((i) => (
                    <Cell key={i} fill={["hsl(var(--muted-foreground))", "hsl(217, 91%, 60%)", "hsl(142, 76%, 36%)", "hsl(38, 92%, 50%)"][i]} />
                  ))}
                </Pie>
              </PieChart>
              <div className="flex-1 text-sm space-y-1">
                <div className="flex justify-between"><span>Free</span><span>{freeBusinessCount}</span></div>
                <div className="flex justify-between"><span>Pro Solo</span><span>{Math.max(0, proCount - proGrowthCount)}</span></div>
                <div className="flex justify-between"><span>Pro Growth</span><span>{proGrowthCount}</span></div>
                <div className="flex justify-between"><span>Founding</span><span>{foundingCount}</span></div>
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground">No data</p>}
        </CardContent>
      </Card>
      )}

      {showOverview && (
      <Card>
        <CardHeader>
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> New Signups
          </CardTitle>
        </CardHeader>
        <CardContent>
          {newSignups?.length ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {newSignups.map((r) => (
                <div key={r.business_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium">{r.business_name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">{r.owner_email ?? "—"} · {r.plan_at_signup} · {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge variant={r.has_activity ? "default" : "secondary"}>{r.has_activity ? "Active" : "Inactive"}</Badge>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No signups yet</p>}
        </CardContent>
      </Card>
      )}

      {(showOverview || showRevenue) && (
      <div data-testid="admin-revenue-panel" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> MRR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p data-testid="admin-mrr-value" className="text-2xl font-bold">K{mrr.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{proCount} paying businesses</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Annual Run Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">K{arr.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Projected from current MRR</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" /> Active Paid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{proCount}</p>
            <p className="text-xs text-muted-foreground">
              {proGrowthCount} Growth, {Math.max(0, proCount - proGrowthCount)} Solo
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Revenue Growth
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${revenueGrowthPct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {revenueGrowthPct >= 0 ? "+" : ""}{revenueGrowthPct.toFixed(1)}%
            </p>
            <p className="text-xs text-muted-foreground">Latest month vs previous collected revenue</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Renewals Due
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{renewalsDueSoon.length}</p>
            <p className="text-xs text-muted-foreground">{churnRiskCount} accounts need attention</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Crown className="h-4 w-4" /> Founding Left
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{foundingSpotsRemaining}</p>
            <p className="text-xs text-muted-foreground">{foundingCount} founding businesses locked in</p>
          </CardContent>
        </Card>
      </div>
      )}

      {(showOverview || showRevenue) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Revenue by month
            </CardTitle>
            <CardDescription>Payments received in Kwacha across completed collections</CardDescription>
          </CardHeader>
          <CardContent>
            {chartRevenue.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No payment data yet</p>
            ) : (
              <ChartContainer id="founder-revenue" config={{ total_k: { label: "Revenue (K)", color: "hsl(142, 76%, 36%)" } }} className="h-[260px] w-full">
                <BarChart data={chartRevenue} margin={{ left: 12, right: 12, top: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `K${v}`} />
                  <Tooltip formatter={(v: number) => [`K${v.toLocaleString()}`, "Revenue"]} labelFormatter={(m) => `Month: ${m}`} />
                  <Bar dataKey="total_k" fill="var(--color-total_k)" radius={[4, 4, 0, 0]} name="Revenue (K)" />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Signups vs upgrades by month
            </CardTitle>
            <CardDescription>Top-of-funnel conversion trend for self-serve growth</CardDescription>
          </CardHeader>
          <CardContent>
            {chartFunnel.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No funnel data yet</p>
            ) : (
              <ChartContainer
                id="founder-funnel"
                config={{
                  signups: { label: "Signups", color: "hsl(217, 91%, 60%)" },
                  upgrades: { label: "Upgrades", color: "hsl(142, 76%, 36%)" },
                }}
                className="h-[260px] w-full"
              >
                <ComposedChart data={chartFunnel} margin={{ left: 12, right: 12, top: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="signups" fill="var(--color-signups)" radius={[4, 4, 0, 0]} name="Signups" />
                  <Bar dataKey="upgrades" fill="var(--color-upgrades)" radius={[4, 4, 0, 0]} name="Upgrades" />
                </ComposedChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {(showOverview || showPlanOverrides) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> SaaS Health Snapshot
            </CardTitle>
            <CardDescription>Key operating metrics founders watch every week</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">ARPU</p>
                <p className="text-xl font-semibold">K{arpu.toLocaleString("en-US", { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Free Businesses</p>
                <p className="text-xl font-semibold">{freeBusinessCount}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">VIP Accounts</p>
                <p className="text-xl font-semibold">{vipCount}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Grace Accounts</p>
                <p className="text-xl font-semibold">{graceCount}</p>
              </div>
            </div>
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Signup to paid conversion</span>
                <span data-testid="admin-conversion-rate" className="font-semibold">{funnel?.pct ?? 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Signups tracked</span>
                <span className="font-semibold">{funnel?.signups ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Upgrades tracked</span>
                <span className="font-semibold">{funnel?.paid ?? 0}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Free: {freeBusinessCount}</Badge>
              <Badge variant="secondary">Pro Solo: {Math.max(0, proCount - proGrowthCount)}</Badge>
              <Badge>Pro Growth + VIP: {proGrowthCount}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Renewal Watchlist
            </CardTitle>
            <CardDescription>Accounts likely to churn soon unless you intervene</CardDescription>
          </CardHeader>
          <CardContent>
            {renewalsDueSoon.length === 0 ? (
              <p className="text-sm text-muted-foreground">No renewals due in the next 7 days.</p>
            ) : (
              <div className="space-y-3">
                {renewalsDueSoon.slice(0, 6).map((row) => (
                  <div key={row.business_id} className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <p className="font-medium">{row.business_name ?? row.email ?? "Unknown business"}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.planLabel} · {row.email ?? "No email"} · {row.expiryDays} day{row.expiryDays === 1 ? "" : "s"} left
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => handleSetVip(row, !row.is_vip)}
                        disabled={actingId !== null}
                      >
                        <Star className="h-3 w-3 mr-1" />
                        {row.is_vip ? "Remove VIP" : "VIP"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => handleExtendGrace(row)}
                        disabled={actingId !== null}
                      >
                        <CalendarPlus className="h-3 w-3 mr-1" />
                        +3 Grace
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {showBusinesses && (
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Business Portfolio
            </CardTitle>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="admin-search-input"
                placeholder="Search business, owner, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <CardDescription>
            One row per business, with plan, renewal, product adoption, and founder actions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-user-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>MRR</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead>Products</TableHead>
                    <TableHead>Signals</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => {
                    const rowMrr =
                      row.planLabel === "Free"
                        ? 0
                        : row.locked_monthly_rate ?? (row.planLabel === "Pro Growth" || row.planLabel === "VIP" ? PRO_GROWTH_MRR : PRO_SOLO_MRR);
                    const lowProducts = row.product_count < 5;
                    return (
                      <TableRow key={row.business_id}>
                        <TableCell className="font-medium">
                          <div className="space-y-1">
                            <p>{row.business_name ?? "—"}</p>
                            <p className="text-xs text-muted-foreground">{row.email ?? "No email"}</p>
                          </div>
                        </TableCell>
                        <TableCell>{row.full_name ?? "—"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge
                              variant={row.planLabel === "Pro Solo" || row.planLabel === "Pro Growth" || row.planLabel === "VIP" ? "default" : row.planLabel === "Grace" ? "secondary" : "outline"}
                            >
                              {row.planLabel}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {rowMrr > 0 ? `K${rowMrr.toLocaleString()}` : "—"}
                        </TableCell>
                        <TableCell>
                          {row.expiryDays !== null ? (
                            <span className={row.expiryDays < 3 ? "text-red-600 font-semibold" : ""}>{row.expiryDays} days</span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {lowProducts ? (
                            <span className="text-amber-600 font-medium">&lt;5</span>
                          ) : (
                            row.product_count
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {row.is_founding_member && <Badge variant="secondary" className="text-[10px]">Founding</Badge>}
                            {row.is_vip && <Badge className="text-[10px]">VIP</Badge>}
                            {(row.planLabel === "Grace" || (row.expiryDays !== null && row.expiryDays <= 7)) && (
                              <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700">At risk</Badge>
                            )}
                            {lowProducts && <Badge variant="outline" className="text-[10px]">Low adoption</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <Button
                              data-testid="admin-vip-toggle"
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs"
                              onClick={() => handleSetVip(row, !row.is_vip)}
                              disabled={actingId !== null}
                            >
                              <Star className="h-3 w-3 mr-1" />
                              {row.is_vip ? "Remove VIP" : "VIP"}
                            </Button>
                            <Button
                              data-testid="admin-grace-extend-btn"
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs"
                              onClick={() => handleExtendGrace(row)}
                              disabled={actingId !== null}
                            >
                              <CalendarPlus className="h-3 w-3 mr-1" />
                              +3 Grace
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {showOverview && (
      <div className="flex flex-wrap items-center gap-4 py-3 px-4 rounded-lg border bg-muted/30 text-sm">
        <span className="text-muted-foreground">Grace period:</span>
        <span className="font-medium">{systemStatus?.grace_count ?? "—"}</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-muted-foreground">Failed payments (month):</span>
        <span className="font-medium">{systemStatus?.failed_payments_this_month ?? "—"}</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-muted-foreground">Last audit:</span>
        <span className="font-medium">{systemStatus?.last_audit_at ? new Date(systemStatus.last_audit_at).toLocaleString() : "—"}</span>
        {systemStatus?.last_audit_action && <span className="text-muted-foreground">({systemStatus.last_audit_action})</span>}
        <span className="ml-auto">
          <a href="https://sentry.io" target="_blank" rel="noreferrer noopener" className="text-primary hover:underline inline-flex items-center gap-1">
            Sentry <ExternalLink className="h-3 w-3" />
          </a>
        </span>
      </div>
      )}
    </div>
  );
}
